import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { 
  AlertTriangle, FileText, Scale, Clock, Send, ChevronRight, 
  CheckCircle2, ArrowRight, Phone, Mail, Building2, Users,
  Calculator, Gavel, FileWarning, Banknote, Calendar, Shield,
  BookOpen, HelpCircle, ExternalLink, Gift
} from 'lucide-react';

const ImpayesCopropriete = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const tableOfContents = [
    { id: 'introduction', title: 'Introduction' },
    { id: 'causes', title: 'Les causes des impayés' },
    { id: 'consequences', title: 'Conséquences pour la copropriété' },
    { id: 'procedure', title: 'Procédure de recouvrement' },
    { id: 'mise-en-demeure', title: 'La mise en demeure' },
    { id: 'relances', title: 'Les relances amiables' },
    { id: 'action-judiciaire', title: 'L\'action en justice' },
    { id: 'privilege', title: 'Le privilège du syndicat' },
    { id: 'prevention', title: 'Prévention des impayés' },
    { id: 'outils', title: 'Nos outils gratuits' },
    { id: 'faq', title: 'FAQ' },
  ];

  const faqs = [
    {
      question: "Au bout de combien de temps un impayé devient-il problématique ?",
      answer: "Dès le premier mois de retard, il est conseillé d'agir. Légalement, vous pouvez envoyer une mise en demeure dès le lendemain de l'échéance. Plus vous attendez, plus le recouvrement sera difficile. La prescription est de 5 ans pour les charges de copropriété."
    },
    {
      question: "Peut-on couper l'eau ou l'électricité d'un copropriétaire qui ne paie pas ?",
      answer: "Non, c'est strictement interdit. Le syndicat des copropriétaires ne peut pas couper les fluides d'un lot, même en cas d'impayés importants. Seules les procédures légales de recouvrement sont autorisées."
    },
    {
      question: "La mise en demeure par email a-t-elle une valeur juridique ?",
      answer: "Un email simple n'a pas de valeur probante suffisante. En revanche, une Lettre Recommandée Électronique (LRE/Clearbus) a exactement la même valeur juridique qu'un recommandé postal depuis le décret du 9 mai 2018. C'est la solution la plus rapide et économique."
    },
    {
      question: "Que faire si le copropriétaire conteste les charges ?",
      answer: "Si la contestation porte sur le montant, vérifiez les tantièmes et le budget voté en AG. Si elle porte sur la nature des travaux, rappelez que les décisions d'AG s'imposent à tous. En cas de désaccord persistant, seul le tribunal peut trancher."
    },
    {
      question: "Peut-on vendre un lot avec des charges impayées ?",
      answer: "Oui, mais le notaire doit demander un état daté au syndic. Les charges impayées seront prélevées sur le prix de vente grâce au privilège spécial du syndicat. Le vendeur ne peut pas échapper à sa dette."
    },
    {
      question: "Le syndic bénévole peut-il gérer les impayés lui-même ?",
      answer: "Absolument ! Le syndic bénévole a les mêmes pouvoirs qu'un syndic professionnel pour le recouvrement : relances, mises en demeure, et même action en justice (avec autorisation de l'AG pour les frais d'avocat)."
    }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Helmet>
        <title>Impayés Copropriété : Guide Complet 2026 | Recouvrement, Mise en Demeure, Procédures</title>
        <meta name="description" content="Guide ultime sur les impayés de copropriété. Procédures de recouvrement, modèles de mise en demeure, relances, action en justice. Conseils d'experts pour syndics bénévoles. Outils gratuits inclus." />
        <meta name="keywords" content="impayés copropriété, copropriétaire ne paie pas charges, mise en demeure copropriété, recouvrement charges copropriété, relance impayés syndic, procédure impayés copropriété, que faire copropriétaire mauvais payeur" />
        <link rel="canonical" href="https://copropriete.app/impayes-copropriete" />
        <meta property="og:title" content="Impayés Copropriété : Guide Complet 2026 | Tout savoir sur le recouvrement" />
        <meta property="og:description" content="Votre copropriétaire ne paie pas ses charges ? Guide complet : procédures, mise en demeure, action en justice. Outils gratuits pour syndics bénévoles." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://copropriete.app/impayes-copropriete" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Impayés Copropriété : Guide Complet 2026",
            "description": "Guide ultime sur les impayés de copropriété. Procédures de recouvrement, modèles de mise en demeure, relances, action en justice.",
            "author": {
              "@type": "Organization",
              "name": "copropriete.app"
            },
            "publisher": {
              "@type": "Organization",
              "name": "copropriete.app",
              "logo": {
                "@type": "ImageObject",
                "url": "https://copropriete.app/logo512.png"
              }
            },
            "datePublished": "2026-01-15",
            "dateModified": "2026-10-02",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://copropriete.app/impayes-copropriete"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://copropriete.app" },
              { "@type": "ListItem", "position": 2, "name": "Guide Impayés Copropriété", "item": "https://copropriete.app/impayes-copropriete" }
            ]
          })}
        </script>
      </Helmet>

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_5700d295-d3ed-4629-8765-e98307778f89/artifacts/bbyq8lme_icone_verre_vert_512.png" 
              alt="copropriete.app" 
              className="h-8 w-8"
            />
            <span className="text-xl font-bold text-slate-900">copropriete.app</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/blog" className="text-slate-600 hover:text-emerald-600 hidden sm:block">Blog</Link>
            <Link to="/login" className="text-slate-600 hover:text-emerald-600">Connexion</Link>
            <Link 
              to="/register" 
              className="bg-emerald-500 text-white px-4 py-2 rounded-lg hover:bg-emerald-600 transition-colors"
            >
              C'est gratuit
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-50 via-orange-50 to-amber-50 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <AlertTriangle className="w-4 h-4" />
            Guide Complet 2026
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            Impayés de Copropriété :<br />
            <span className="text-red-600">Le Guide Ultime</span>
          </h1>
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
            Votre copropriétaire ne paie pas ses charges ? Découvrez toutes les procédures de recouvrement, 
            de la relance amiable à l'action en justice. <strong>Outils gratuits inclus.</strong>
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/generateur-mise-en-demeure')}
              className="inline-flex items-center justify-center gap-2 bg-red-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-red-700 transition-all shadow-lg hover:shadow-xl"
            >
              <FileWarning className="w-5 h-5" />
              Créer une mise en demeure
              <ArrowRight className="w-5 h-5" />
            </button>
            <button 
              onClick={() => scrollToSection('procedure')}
              className="inline-flex items-center justify-center gap-2 bg-white text-slate-700 px-8 py-4 rounded-xl font-semibold hover:bg-slate-50 transition-all border border-slate-200"
            >
              <BookOpen className="w-5 h-5" />
              Lire le guide
            </button>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-slate-900 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-bold text-emerald-400">15%</div>
              <div className="text-slate-400 text-sm">des copropriétés touchées</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-emerald-400">5 ans</div>
              <div className="text-slate-400 text-sm">de prescription</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-emerald-400">30 jours</div>
              <div className="text-slate-400 text-sm">délai mise en demeure</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-emerald-400">8,90€</div>
              <div className="text-slate-400 text-sm">coût AR électronique</div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-12">
          
          {/* Table of Contents - Sticky Sidebar */}
          <aside className="lg:w-72 flex-shrink-0">
            <div className="lg:sticky lg:top-24">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-emerald-500" />
                  Sommaire
                </h2>
                <nav className="space-y-2">
                  {tableOfContents.map((item, index) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className="block w-full text-left text-sm text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 px-3 py-2 rounded-lg transition-colors"
                    >
                      <span className="text-slate-400 mr-2">{index + 1}.</span>
                      {item.title}
                    </button>
                  ))}
                </nav>
              </div>

              {/* CTA Box */}
              <div className="mt-6 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-6 text-white">
                <Gift className="w-8 h-8 mb-3" />
                <h3 className="font-bold text-lg mb-2">Outil 100% Gratuit</h3>
                <p className="text-emerald-100 text-sm mb-4">
                  Générez votre mise en demeure en 2 minutes avec notre assistant guidé.
                </p>
                <button 
                  onClick={() => navigate('/generateur-mise-en-demeure')}
                  className="w-full bg-white text-emerald-600 font-semibold py-3 rounded-xl hover:bg-emerald-50 transition-colors"
                >
                  Créer ma mise en demeure
                </button>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 max-w-3xl">
            <article className="prose prose-slate prose-lg max-w-none">
              
              {/* Introduction */}
              <section id="introduction" className="mb-16">
                <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center text-lg font-bold">1</span>
                  Introduction : Un fléau qui touche 15% des copropriétés
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Les <strong>impayés de charges de copropriété</strong> représentent l'un des problèmes les plus fréquents 
                  et les plus délicats à gérer pour les syndics, qu'ils soient professionnels ou bénévoles. 
                  Selon les dernières études, <strong>près de 15% des copropriétés françaises</strong> sont confrontées 
                  à des situations d'impayés significatifs.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  Ces impayés peuvent rapidement mettre en péril l'équilibre financier de la copropriété : 
                  travaux reportés, trésorerie insuffisante, appels de fonds supplémentaires pour les copropriétaires 
                  à jour de leurs paiements... Les conséquences sont multiples et peuvent créer des tensions 
                  importantes au sein de la copropriété.
                </p>
                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-xl my-6">
                  <p className="text-amber-800 font-medium mb-0">
                    💡 <strong>Bon à savoir :</strong> En tant que syndic bénévole, vous avez exactement les mêmes 
                    pouvoirs qu'un syndic professionnel pour recouvrer les impayés. Ce guide vous accompagne 
                    étape par étape.
                  </p>
                </div>
              </section>

              {/* Causes */}
              <section id="causes" className="mb-16">
                <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center text-lg font-bold">2</span>
                  Les causes fréquentes des impayés
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Comprendre les raisons des impayés permet d'adapter sa stratégie de recouvrement. 
                  Voici les situations les plus courantes :
                </p>
                <div className="grid gap-4">
                  {[
                    { icon: Banknote, title: "Difficultés financières", desc: "Perte d'emploi, divorce, maladie... Le copropriétaire n'a temporairement plus les moyens de payer." },
                    { icon: HelpCircle, title: "Contestation des charges", desc: "Le copropriétaire estime que les charges sont injustifiées ou mal calculées." },
                    { icon: Users, title: "Conflit avec le syndic", desc: "Un désaccord personnel qui se traduit par un refus de payer." },
                    { icon: Building2, title: "Négligence ou oubli", desc: "Le copropriétaire absent ou peu impliqué oublie simplement de régler." },
                    { icon: Scale, title: "Succession en cours", desc: "Le lot est en indivision et personne ne se sent responsable." },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4 p-4 bg-slate-50 rounded-xl">
                      <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                        <item.icon className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">{item.title}</h4>
                        <p className="text-slate-600 text-sm mt-1">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Conséquences */}
              <section id="consequences" className="mb-16">
                <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center text-lg font-bold">3</span>
                  Conséquences pour la copropriété
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Les impayés ont des répercussions directes sur l'ensemble des copropriétaires :
                </p>
                <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Trésorerie insuffisante</strong> : impossibilité de payer les fournisseurs (gardien, entretien, énergie)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Travaux reportés</strong> : l'entretien de l'immeuble est différé, aggravant les dégradations</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Appels de fonds supplémentaires</strong> : les copropriétaires à jour doivent compenser</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Tensions internes</strong> : conflits entre copropriétaires, climat délétère</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Dévalorisation du bien</strong> : un immeuble mal entretenu perd de sa valeur</span>
                    </li>
                  </ul>
                </div>
              </section>

              {/* Procédure */}
              <section id="procedure" className="mb-16">
                <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center text-lg font-bold">4</span>
                  La procédure de recouvrement étape par étape
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Voici la procédure recommandée, de la plus simple à la plus contraignante :
                </p>
                <div className="relative">
                  <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-emerald-200"></div>
                  {[
                    { step: 1, title: "Relance amiable", delay: "J+7", desc: "Email ou courrier simple rappelant l'échéance passée." },
                    { step: 2, title: "Relance téléphonique", delay: "J+15", desc: "Contact direct pour comprendre la situation et proposer des solutions." },
                    { step: 3, title: "Mise en demeure", delay: "J+30", desc: "Lettre recommandée avec AR exigeant le paiement sous 30 jours.", important: true },
                    { step: 4, title: "Injonction de payer", delay: "J+60", desc: "Procédure simplifiée auprès du tribunal (sans avocat obligatoire)." },
                    { step: 5, title: "Action en justice", delay: "J+90", desc: "Assignation devant le tribunal si l'injonction est contestée." },
                  ].map((item, i) => (
                    <div key={i} className={`relative pl-16 pb-8 ${item.important ? 'bg-emerald-50 -ml-4 pl-20 py-4 rounded-xl' : ''}`}>
                      <div className={`absolute left-4 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${item.important ? 'bg-emerald-500 text-white' : 'bg-emerald-100 text-emerald-600'}`}>
                        {item.step}
                      </div>
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-bold text-slate-900">{item.title}</h4>
                        <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded-full">{item.delay}</span>
                      </div>
                      <p className="text-slate-600 text-sm">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Mise en demeure */}
              <section id="mise-en-demeure" className="mb-16">
                <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center text-lg font-bold">5</span>
                  La mise en demeure : l'étape clé
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  La <strong>mise en demeure</strong> est l'étape cruciale du recouvrement. C'est un courrier formel 
                  qui somme le débiteur de payer sa dette dans un délai précis. Elle a plusieurs effets juridiques importants :
                </p>
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  {[
                    { icon: Scale, text: "Fait courir les intérêts de retard" },
                    { icon: FileText, text: "Constitue une preuve en cas de procès" },
                    { icon: Clock, text: "Interrompt la prescription de 5 ans" },
                    { icon: Gavel, text: "Préalable obligatoire à l'action en justice" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-4 bg-emerald-50 rounded-xl">
                      <item.icon className="w-5 h-5 text-emerald-600" />
                      <span className="text-slate-700 font-medium">{item.text}</span>
                    </div>
                  ))}
                </div>
                
                <div className="bg-slate-900 text-white rounded-2xl p-6 md:p-8">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <Send className="w-6 h-6 text-emerald-400" />
                    Envoyez votre mise en demeure en 2 minutes
                  </h3>
                  <p className="text-slate-300 mb-6">
                    Notre assistant guidé vous aide à rédiger une mise en demeure conforme, 
                    puis l'envoie en recommandé électronique (Clearbus) avec accusé de réception.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button 
                      onClick={() => navigate('/generateur-mise-en-demeure')}
                      className="inline-flex items-center justify-center gap-2 bg-emerald-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-emerald-600 transition-all"
                    >
                      Créer ma mise en demeure
                      <ArrowRight className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-2 text-slate-400 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Valeur juridique identique au recommandé postal
                    </div>
                  </div>
                </div>
              </section>

              {/* Relances */}
              <section id="relances" className="mb-16">
                <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center text-lg font-bold">6</span>
                  Les relances amiables
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Avant d'en arriver à la mise en demeure, plusieurs relances amiables sont recommandées. 
                  Elles permettent souvent de résoudre la situation sans frais supplémentaires.
                </p>
                <div className="space-y-4">
                  <div className="border border-slate-200 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <Mail className="w-5 h-5 text-blue-500" />
                      <h4 className="font-bold text-slate-900">Relance par email (J+7)</h4>
                    </div>
                    <p className="text-slate-600 text-sm mb-3">
                      Un simple rappel, cordial mais ferme, de l'échéance passée. Joignez le relevé de compte.
                    </p>
                    <div className="bg-slate-50 p-4 rounded-lg text-sm font-mono text-slate-700">
                      Objet : Rappel - Charges de copropriété impayées<br/><br/>
                      Madame, Monsieur,<br/>
                      Nous constatons que votre appel de fonds du [date] d'un montant de [X] € reste impayé à ce jour...
                    </div>
                  </div>
                  <div className="border border-slate-200 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <Phone className="w-5 h-5 text-green-500" />
                      <h4 className="font-bold text-slate-900">Relance téléphonique (J+15)</h4>
                    </div>
                    <p className="text-slate-600 text-sm">
                      Un appel permet de comprendre la situation : difficultés passagères ? Oubli ? Contestation ? 
                      C'est l'occasion de proposer un échéancier si le copropriétaire est de bonne foi.
                    </p>
                  </div>
                </div>
              </section>

              {/* Action judiciaire */}
              <section id="action-judiciaire" className="mb-16">
                <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center text-lg font-bold">7</span>
                  L'action en justice
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Si la mise en demeure reste sans effet après 30 jours, vous pouvez engager une procédure judiciaire. 
                  Deux options s'offrent à vous :
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="border-2 border-emerald-200 rounded-2xl p-6 bg-emerald-50/50">
                    <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      Injonction de payer (recommandé)
                    </h4>
                    <ul className="text-slate-600 text-sm space-y-2">
                      <li>• Procédure simplifiée et rapide</li>
                      <li>• Sans avocat obligatoire</li>
                      <li>• Coût : environ 40€ de frais de greffe</li>
                      <li>• Délai : 1 à 3 mois</li>
                      <li>• Le débiteur peut faire opposition</li>
                    </ul>
                  </div>
                  <div className="border border-slate-200 rounded-2xl p-6">
                    <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                      <Gavel className="w-5 h-5 text-slate-500" />
                      Assignation au tribunal
                    </h4>
                    <ul className="text-slate-600 text-sm space-y-2">
                      <li>• Procédure plus longue et coûteuse</li>
                      <li>• Avocat recommandé (souvent obligatoire)</li>
                      <li>• Coût : 500€ à 2000€+</li>
                      <li>• Délai : 6 mois à 2 ans</li>
                      <li>• Permet de réclamer des dommages</li>
                    </ul>
                  </div>
                </div>
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-xl mt-6">
                  <p className="text-blue-800 mb-0">
                    <strong>💡 Conseil :</strong> L'injonction de payer est efficace dans 80% des cas. 
                    Réservez l'assignation aux dossiers complexes ou aux montants importants (&gt;5000€).
                  </p>
                </div>
              </section>

              {/* Privilège */}
              <section id="privilege" className="mb-16">
                <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center text-lg font-bold">8</span>
                  Le privilège spécial du syndicat
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Le syndicat des copropriétaires bénéficie d'un <strong>privilège spécial immobilier</strong> 
                  (article 2374 du Code civil) qui lui garantit d'être payé en priorité sur le prix de vente du lot.
                </p>
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6">
                  <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-amber-600" />
                    Ce que garantit ce privilège :
                  </h4>
                  <ul className="text-slate-700 space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span>Les charges de l'année en cours + les 2 années précédentes sont récupérables en priorité</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span>Le notaire doit demander un <strong>état daté</strong> avant toute vente</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span>Les sommes dues sont prélevées sur le prix de vente avant versement au vendeur</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span>Ce privilège prime sur la plupart des autres créanciers (même les banques !)</span>
                    </li>
                  </ul>
                </div>
              </section>

              {/* Prévention */}
              <section id="prevention" className="mb-16">
                <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center text-lg font-bold">9</span>
                  Prévenir les impayés
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  La meilleure stratégie reste la prévention. Voici les bonnes pratiques :
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { icon: Calendar, title: "Appels de fonds trimestriels", desc: "Répartir les charges sur 4 échéances facilite le paiement" },
                    { icon: Mail, title: "Rappels automatiques", desc: "Envoyer un rappel 7 jours avant chaque échéance" },
                    { icon: Calculator, title: "Budget réaliste", desc: "Un budget bien calibré évite les appels exceptionnels" },
                    { icon: Users, title: "Communication transparente", desc: "Informer régulièrement les copropriétaires des finances" },
                  ].map((item, i) => (
                    <div key={i} className="p-5 border border-slate-200 rounded-xl hover:border-emerald-300 hover:bg-emerald-50/50 transition-colors">
                      <item.icon className="w-8 h-8 text-emerald-500 mb-3" />
                      <h4 className="font-bold text-slate-900 mb-1">{item.title}</h4>
                      <p className="text-slate-600 text-sm">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Outils */}
              <section id="outils" className="mb-16">
                <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center text-lg font-bold">10</span>
                  Nos outils gratuits
                </h2>
                <div className="grid gap-4">
                  <Link 
                    to="/generateur-mise-en-demeure"
                    className="group flex items-center gap-4 p-6 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl text-white hover:shadow-lg transition-all"
                  >
                    <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                      <FileWarning className="w-7 h-7" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-lg">Générateur de mise en demeure</h4>
                      <p className="text-emerald-100 text-sm">Créez une mise en demeure conforme en 2 minutes</p>
                    </div>
                    <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link 
                    to="/register"
                    className="group flex items-center gap-4 p-6 border-2 border-emerald-200 rounded-2xl hover:border-emerald-400 hover:bg-emerald-50 transition-all"
                  >
                    <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <Building2 className="w-7 h-7 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-slate-900 text-lg">Application de gestion complète</h4>
                      <p className="text-slate-600 text-sm">Gérez vos appels de fonds, relances et impayés — 100% gratuit</p>
                    </div>
                    <ArrowRight className="w-6 h-6 text-emerald-500 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </section>

              {/* FAQ */}
              <section id="faq" className="mb-16">
                <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center text-lg font-bold">11</span>
                  Questions fréquentes
                </h2>
                <div className="space-y-3">
                  {faqs.map((faq, i) => (
                    <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                      <button
                        onClick={() => setOpenFaq(openFaq === i ? null : i)}
                        className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
                      >
                        <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${openFaq === i ? 'rotate-90' : ''}`} />
                      </button>
                      {openFaq === i && (
                        <div className="px-5 pb-5 text-slate-600">
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

            </article>
          </main>
        </div>
      </div>

      {/* Bottom CTA */}
      <section className="bg-slate-900 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Prêt à récupérer vos impayés ?
          </h2>
          <p className="text-slate-400 text-lg mb-8">
            Créez votre mise en demeure en 2 minutes et envoyez-la en recommandé électronique.
          </p>
          <button 
            onClick={() => navigate('/generateur-mise-en-demeure')}
            className="inline-flex items-center gap-2 bg-emerald-500 text-white px-8 py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-all text-lg"
          >
            <FileWarning className="w-6 h-6" />
            Créer ma mise en demeure
            <ArrowRight className="w-6 h-6" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_5700d295-d3ed-4629-8765-e98307778f89/artifacts/bbyq8lme_icone_verre_vert_512.png" 
                alt="copropriete.app" 
                className="h-8 w-8"
              />
              <span className="text-white font-bold">copropriete.app</span>
            </Link>
            <div className="flex gap-6 text-slate-400 text-sm">
              <Link to="/mentions-legales" className="hover:text-white">Mentions légales</Link>
              <Link to="/cgu" className="hover:text-white">CGU</Link>
              <Link to="/blog" className="hover:text-white">Blog</Link>
            </div>
            <p className="text-slate-500 text-sm">© 2026 GMP Développement</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ImpayesCopropriete;
